'use client';

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { watchlistApi } from '@/lib/api/watchlistApi';
import { StockNote, WatchlistInfo } from '@/types/watchlist';

interface StockNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  isin: string;
  stockName: string;
  watchlistId?: string; // Optional - if provided, will use watchlist-specific notes
  currentPrice?: number;
}

export default function StockNotesModal({
  isOpen,
  onClose,
  isin,
  stockName,
  watchlistId,
  currentPrice
}: StockNotesModalProps) {
  const [stockNotes, setStockNotes] = useState<StockNote[]>([]);
  const [watchlistInfo, setWatchlistInfo] = useState<WatchlistInfo[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');

  const loadNotes = useCallback(async () => {
    if (!isOpen || !isin) return;
    
    setNotesLoading(true);
    setNotesError(null);

    try {
      // Use notes API - no longer needs watchlistId
      const response = await watchlistApi.getNotes(isin);
      setStockNotes(response.notes);
      setWatchlistInfo(response.stockPresentInWatchlist);
    } catch (err) {
      setNotesError('Failed to load notes');
      console.error('Error loading notes:', err);
    } finally {
      setNotesLoading(false);
    }
  }, [isOpen, isin]);

  const addNewNote = useCallback(async () => {
    if (!newNote.trim() || !isin) return;
    
    try {
      // Use notes API - no longer needs watchlistId  
      await watchlistApi.addNote(isin, newNote);
      await loadNotes(); // Reload notes after adding
      
      setNewNote('');
      setNotesError(null);
    } catch (err) {
      setNotesError('Failed to add note');
      console.error('Error adding note:', err);
    }
  }, [newNote, isin, loadNotes]);

  useEffect(() => {
    if (isOpen) {
      loadNotes();
      setNewNote('');
      setNotesError(null);
    }
  }, [isOpen, loadNotes]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
        <div className="sticky top-0 flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800 bg-white dark:bg-zinc-900 z-[10000]">
          <div>
            <h3 className="text-xl font-bold text-zinc-900 dark:text-white">
              {stockName || isin}
            </h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Investment Notes & Analysis
            </p>
            {watchlistInfo.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">Present in watchlists:</p>
                <div className="flex flex-wrap gap-2">
                  {watchlistInfo.map((watchlist, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20"
                    >
                      {watchlist.watchlist_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 dark:text-zinc-400 dark:hover:text-zinc-300 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6">
          {notesError && (
            <div className="mb-6 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4">
              <p className="text-red-700 dark:text-red-400 font-medium">{notesError}</p>
            </div>
          )}
          
          {notesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent mr-3" />
              <span className="text-zinc-600 dark:text-zinc-400">Loading notes...</span>
            </div>
          ) : (
            <>
              {stockNotes.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-zinc-900 dark:text-white mb-2">No notes yet</h4>
                  <p className="text-zinc-500 dark:text-zinc-400 mb-6">Start tracking your investment thoughts and decisions for this stock.</p>
                </div>
              ) : (
                <div className="space-y-4 mb-6">
                  {stockNotes.map((note, index) => (
                    <div key={index} className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-5 border border-zinc-200 dark:border-zinc-800 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-500/15 rounded-full flex items-center justify-center">
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-sm">
                              {note.added_by?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900 dark:text-white">{note.added_by}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                              {new Date(note.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-zinc-500 dark:text-zinc-400">Price at note</p>
                          <p className="font-semibold text-zinc-900 dark:text-white">
                            ₹{note.price ? note.price.toFixed(2) : 'N/A'}
                          </p>
                          {currentPrice && note.price && (
                            <p className={`text-xs ${
                              currentPrice > note.price 
                                ? 'text-green-600 dark:text-green-400' 
                                : currentPrice < note.price 
                                  ? 'text-red-600 dark:text-red-400' 
                                  : 'text-zinc-500 dark:text-zinc-400'
                            }`}>
                              {currentPrice > note.price && '+'}
                              {((currentPrice - note.price) / note.price * 100).toFixed(1)}%
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="bg-white dark:bg-zinc-900 rounded-lg p-4 border border-zinc-200 dark:border-zinc-800">
                        <p className="text-zinc-800 dark:text-zinc-200 leading-relaxed">{note.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Note Section */}
              <div className="border-t border-zinc-200 dark:border-zinc-800 pt-6">
                <h4 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4">Add New Note</h4>
                <div className="space-y-4">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Share your analysis, thoughts, or decision rationale for this stock..."
                    rows={4}
                    className="w-full rounded-lg border border-zinc-300 bg-white p-4 text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none transition-colors"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                      {currentPrice ? `Current price: ₹${currentPrice.toFixed(2)}` : ''}
                    </p>
                    <button
                      onClick={addNewNote}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      disabled={notesLoading || !newNote.trim()}
                    >
                      {notesLoading ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <PlusIcon className="h-4 w-4" />
                          Add Note
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document root level, escaping table stacking context
  return typeof window !== 'undefined' ? createPortal(modalContent, document.body) : null;
} 